import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaBarsStaggered, FaWhatsapp } from "react-icons/fa6";
import {
  FiUsers, FiCheckCircle, FiActivity, FiLayers, FiRefreshCw, FiCopy, FiCheck, FiSearch, FiX,
  FiChevronLeft, FiChevronRight, FiExternalLink, FiBookOpen, FiClock, FiCalendar,
} from "react-icons/fi";
import { QRCodeSVG } from "qrcode.react";
import SideBar from "./SideBar";
import { useGetSwachhataSchoolStudentsQuery } from "../../redux/auth/authApi";
import { SWACHHATA_QUIZ_OPEN, SWACHHATA_SWM_REPORT_OPEN } from "../../config/campaigns";
import ecoLogo from "../../assets/images/Logo-2.png";
import "./Swachhata/Swachhata.css";

/*
 * /school/swachhata-students — the school's view of its students' participation
 * in the public Swachhata Hi Seva quiz: who took part (name, class, age, when)
 * and how many. Marks and badges are deliberately not shown to schools.
 *
 * Every mixed text line is ONE template string: the Anuvadini translation
 * extension merges adjacent JSX text nodes and drops the spaces between them.
 */

const QUIZ_HOST = "ecoclubs.education.gov.in";
const QUIZ_URL = `https://${QUIZ_HOST}/swachhata-hi-seva`;
const CLASSES = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "Other"];
const PAGE_SIZE = 25;
const COMPLETED = "#2fb457";
const IN_PROGRESS = "#f0a53a";
const AVATAR_TONES = ["green", "teal", "amber", "blue"];

const nf = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "");
const fmtStamp = (iso) => (iso ? `${fmtDay(iso)}, ${fmtTime(iso)}` : "—");
const classLabel = (c) => (c === "Other" ? "Other" : `Class ${c}`);
const plural = (n, word) => `${nf(n)} ${word}${n === 1 ? "" : "s"}`;
const initials = (name) => String(name || "").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
const toneOf = (name) => AVATAR_TONES[[...String(name || "")].reduce((h, ch) => (h * 31 + ch.charCodeAt(0)) >>> 0, 7) % AVATAR_TONES.length];

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.cssText = "position:fixed;opacity:0;top:0;left:0";
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try { ok = document.execCommand("copy"); } catch { ok = false; }
    ta.remove();
    return ok;
  }
}

function CopyButton({ text, label = "Copy" }) {
  const [done, setDone] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <button
      type="button"
      className={`shsp-copy ${done ? "is-done" : ""}`}
      onClick={async () => {
        if (!(await copyText(text))) return;
        setDone(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setDone(false), 1800);
      }}
    >
      {done ? <FiCheck /> : <FiCopy />}
      <span>{done ? "Copied" : label}</span>
    </button>
  );
}

/* Axis top: a round step with at least two gridlines above the baseline. */
function niceMax(m) {
  const step = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 5000, 10000].find((s) => m / s <= 4) || Math.ceil(m / 4);
  return { max: Math.max(Math.ceil(m / step) * step, step * 2), step };
}

function ClassChart({ classes }) {
  const byClass = new Map(classes.map((c) => [String(c.class), c]));
  const { max, step } = niceMax(Math.max(1, ...classes.map((c) => c.attempts)));
  const ticks = [];
  for (let t = 0; t <= max; t += step) ticks.push(t);
  return (
    <div className="shsp-chart" role="img" aria-label={classes.map((c) => `${classLabel(c.class)}: ${plural(c.attempts, "attempt")}`).join(", ") || "No participation yet"}>
      <div className="shsp-chart__grid" aria-hidden="true">
        {ticks.map((t) => (
          <div key={t} className={`shsp-chart__line ${t === 0 ? "is-base" : ""}`} style={{ bottom: `${(t / max) * 100}%` }}>
            <span>{t}</span>
          </div>
        ))}
      </div>
      <div className="shsp-chart__cols">
        {CLASSES.map((c, i) => {
          const r = byClass.get(c);
          const total = r?.attempts || 0;
          const done = r?.completed || 0;
          const open = total - done;
          const edge = i < 2 ? "is-left" : i > CLASSES.length - 3 ? "is-right" : "";
          return (
            <div key={c} className={`shsp-col ${total ? "" : "is-empty"} ${edge}`} tabIndex={total ? 0 : -1}>
              <div className="shsp-col__area">
                <div className="shsp-col__stack" style={{ height: `${(total / max) * 100}%` }}>
                  {total > 0 && <span className="shsp-col__val">{total}</span>}
                  {open > 0 && <i className="shsp-col__seg" style={{ flexGrow: open, background: IN_PROGRESS }} />}
                  {done > 0 && <i className="shsp-col__seg" style={{ flexGrow: done, background: COMPLETED }} />}
                  <div className="shsp-col__tip" role="tooltip">
                    <b>{classLabel(c)}</b>
                    <span>{plural(total, "attempt")}</span>
                    {total > 0 && <span>{`${nf(done)} completed · ${nf(open)} in progress`}</span>}
                  </div>
                </div>
              </div>
              <span className="shsp-col__label">{c}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatusDonut({ completed, attempts }) {
  const open = Math.max(0, attempts - completed);
  const pct = attempts ? Math.round((completed / attempts) * 100) : null;
  const R = 52;
  const C = 2 * Math.PI * R;
  const gap = completed && open ? 2 : 0;
  const doneLen = attempts ? (C * completed) / attempts : 0;
  const openLen = attempts ? (C * open) / attempts : 0;
  return (
    <div className="shsp-donut">
      <div className="shsp-donut__svg">
        <svg viewBox="0 0 128 128" aria-hidden="true">
          <circle cx="64" cy="64" r={R} fill="none" stroke="#edf3ee" strokeWidth="14" />
          {doneLen > 0 && <circle cx="64" cy="64" r={R} fill="none" stroke={COMPLETED} strokeWidth="14" strokeDasharray={`${Math.max(0, doneLen - gap)} ${C}`} />}
          {openLen > 0 && <circle cx="64" cy="64" r={R} fill="none" stroke={IN_PROGRESS} strokeWidth="14" strokeDasharray={`${Math.max(0, openLen - gap)} ${C}`} strokeDashoffset={-doneLen} />}
        </svg>
        <div className="shsp-donut__center">
          <b>{pct === null ? "—" : `${pct}%`}</b>
          <span>completed</span>
        </div>
      </div>
      <div className="shsp-donut__legend">
        <div className="shsp-donut__row"><i style={{ "--c": COMPLETED }} /><span>Completed</span><b>{nf(completed)}</b></div>
        <div className="shsp-donut__row"><i style={{ "--c": IN_PROGRESS }} /><span>In progress</span><b>{nf(open)}</b></div>
        <div className="shsp-donut__row shsp-donut__row--total"><i style={{ "--c": "transparent" }} /><span>Total attempts</span><b>{nf(attempts)}</b></div>
      </div>
    </div>
  );
}

function pageWindow(page, totalPages) {
  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
  const end = Math.min(totalPages, start + 4);
  const out = [];
  for (let p = start; p <= end; p += 1) out.push(p);
  return out;
}

export default function SwachhataStudents() {
  const user = useSelector((s) => s.auth.user);
  const [openSidebar, setOpenSidebar] = useState(false);
  const [search, setSearch] = useState("");
  const [klass, setKlass] = useState("");
  const [page, setPage] = useState(1);
  const [debounced, setDebounced] = useState("");
  useEffect(() => { const t = setTimeout(() => setDebounced(search.trim()), 350); return () => clearTimeout(t); }, [search]);

  const { data, isFetching, refetch } = useGetSwachhataSchoolStudentsQuery({ page, limit: PAGE_SIZE, search: debounced, class: klass });
  const loaded = !!data;
  const summary = data?.summary || { attempts: 0, completed: 0, students: 0, classesCovered: 0, firstAt: null, lastAt: null };
  const classes = data?.classes || [];
  const rows = data?.rows || [];
  const total = data?.total || 0;
  const totalPages = data?.totalPages || 1;
  const udise = String(data?.udise_id || user?.udise_id || "");
  const schoolName = user?.school_name || "your school";
  const openCount = Math.max(0, summary.attempts - summary.completed);
  const completionPct = summary.attempts ? Math.round((summary.completed / summary.attempts) * 100) : 0;
  const covered = new Set(classes.map((c) => String(c.class)));
  const filtered = !!(debounced || klass);
  const from = total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const to = Math.min(total, page * PAGE_SIZE);
  const shareText = `Swachhata Hi Seva quiz: open ${QUIZ_URL} and enter our school's UDISE code ${udise} to take part.`;
  const value = (n) => (loaded ? nf(n) : <i className="shsp-skel shsp-skel--num" />);

  return (
    <section className="scholdashbrdSec">
      <div className="desktopView" style={{ marginTop: 70, textAlign: "end" }}>
        <Link className="button green bars" to="#" onClick={(e) => { e.preventDefault(); setOpenSidebar((o) => !o); }}>
          <FaBarsStaggered size={20} />
        </Link>
      </div>
      <div className={`sidebar ${openSidebar ? "open" : "hideen"}`}>
        <SideBar index={10} role={user?.role} />
      </div>
      {openSidebar && <div className="sidebar-backdrop" onClick={() => setOpenSidebar(false)} aria-hidden="true" />}

      <div className="schlDashbrdRgt">
        <div className="shs-students">
          {/* a <div>, not <header>: the portal styles bare header elements sticky with a gradient */}
          <div className={`shsp-hero shsp-rise${SWACHHATA_QUIZ_OPEN ? "" : " shsp-hero--solo"}`}>
            <svg className="shsp-hero__art" viewBox="0 0 200 200" aria-hidden="true">
              <path d="M100 8c52 22 84 62 84 108 0 42-34 76-84 76S16 158 16 116C16 70 48 30 100 8Z" fill="none" stroke="#fff" strokeWidth="2" />
              <path d="M100 30v160M100 70l-36-28M100 70l36-28M100 110l-50-34M100 110l50-34M100 150l-58-36M100 150l58-36" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <div className="shsp-hero__main">
              <div className="shsp-brand">
                <span className="shsp-brand__plate"><img src={ecoLogo} alt="Eco Clubs for Mission LiFE" /></span>
                <span className="shsp-brand__text">Swachhata Hi Seva · Student participation</span>
              </div>
              <h1>{`Students of ${schoolName} in the Swachhata Hi Seva quiz`}</h1>
              <p className="shsp-hero__lead">
                {SWACHHATA_QUIZ_OPEN
                  ? "Students open the quiz page, read the learning material and take the quiz after entering your school's UDISE code. This register shows who took part and when. Individual scores are not shown to schools."
                  : "The Swachhata Hi Seva quiz is now closed. This register shows which of your students took part and when. Individual scores are not shown to schools."}
              </p>
              <div className="shsp-hero__meta">
                <span className="shsp-meta"><FiBookOpen /><span>{`UDISE code ${udise || "—"}`}</span></span>
                <span className="shsp-meta"><FiClock /><span>{summary.lastAt ? `Latest participation ${fmtStamp(summary.lastAt)}` : "No participation yet"}</span></span>
              </div>
              <div className="shsp-hero__actions">
                {SWACHHATA_SWM_REPORT_OPEN && (
                  <button type="button" className="shsp-btn shsp-btn--sun" onClick={() => window.dispatchEvent(new Event("openSwachhataModal"))}>
                    <FiBookOpen /><span>School report form</span>
                  </button>
                )}
                <button type="button" className="shsp-btn shsp-btn--glass" onClick={() => refetch()} disabled={isFetching}>
                  <FiRefreshCw className={isFetching ? "is-spinning" : ""} /><span>{isFetching ? "Refreshing" : "Refresh"}</span>
                </button>
              </div>
            </div>

            {SWACHHATA_QUIZ_OPEN && (
            <div className="shsp-share">
              <div className="shsp-share__top">
                <div className="shsp-share__qr"><QRCodeSVG value={QUIZ_URL} size={104} level="M" bgColor="#ffffff" fgColor="#14301f" /></div>
                <div>
                  <span className="shsp-share__eyebrow">Invite your students</span>
                  <h2>Share the quiz link</h2>
                  <p className="shsp-share__hint">{"Students scan the code or open the link, then enter your school's UDISE code to be counted here."}</p>
                </div>
              </div>
              <div className="shsp-copyrow">
                <span className="shsp-copyrow__label">Link</span>
                <span className="shsp-copyrow__value">{`${QUIZ_HOST}/swachhata-hi-seva`}</span>
                <CopyButton text={QUIZ_URL} />
              </div>
              <div className="shsp-copyrow">
                <span className="shsp-copyrow__label">UDISE</span>
                <span className="shsp-copyrow__value shsp-copyrow__value--mono">{udise || "—"}</span>
                {udise && <CopyButton text={udise} />}
              </div>
              <div className="shsp-share__actions">
                <a className="shsp-btn shsp-btn--wa" href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noopener noreferrer">
                  <FaWhatsapp /><span>Share on WhatsApp</span>
                </a>
                <a className="shsp-btn shsp-btn--outline" href="/swachhata-hi-seva" target="_blank" rel="noopener noreferrer">
                  <FiExternalLink /><span>Open quiz page</span>
                </a>
              </div>
            </div>
            )}
          </div>

          <div className="shsp-kpis">
            <div className="shsp-kpi shsp-kpi--green shsp-rise" style={{ animationDelay: "60ms" }}>
              <div className="shsp-kpi__top"><span className="shsp-kpi__label">Students participated</span><span className="shsp-kpi__icon"><FiUsers /></span></div>
              <div className="shsp-kpi__value">{value(summary.students)}</div>
              <div className="shsp-kpi__sub">Counted once per student name</div>
            </div>
            <div className="shsp-kpi shsp-kpi--teal shsp-rise" style={{ animationDelay: "120ms" }}>
              <div className="shsp-kpi__top"><span className="shsp-kpi__label">Quizzes completed</span><span className="shsp-kpi__icon"><FiCheckCircle /></span></div>
              <div className="shsp-kpi__value">{value(summary.completed)}</div>
              <div className="shsp-kpi__sub">
                <div className="shsp-meter"><b style={{ width: `${completionPct}%` }} /></div>
                <span>{`${completionPct}% of attempts completed`}</span>
              </div>
            </div>
            <div className="shsp-kpi shsp-kpi--amber shsp-rise" style={{ animationDelay: "180ms" }}>
              <div className="shsp-kpi__top"><span className="shsp-kpi__label">Total attempts</span><span className="shsp-kpi__icon"><FiActivity /></span></div>
              <div className="shsp-kpi__value">{value(summary.attempts)}</div>
              <div className="shsp-kpi__sub">{openCount ? `${nf(openCount)} still in progress` : "None left in progress"}</div>
            </div>
            <div className="shsp-kpi shsp-kpi--blue shsp-rise" style={{ animationDelay: "240ms" }}>
              <div className="shsp-kpi__top"><span className="shsp-kpi__label">Classes covered</span><span className="shsp-kpi__icon"><FiLayers /></span></div>
              <div className="shsp-kpi__value">{value(summary.classesCovered)}</div>
              <div className="shsp-kpi__sub">
                <div className="shsp-classdots" aria-label={`Classes covered: ${[...covered].map(classLabel).join(", ") || "none"}`}>
                  {CLASSES.map((c) => <span key={c} className={covered.has(c) ? "is-on" : ""} title={classLabel(c)}>{c === "Other" ? "O" : c}</span>)}
                </div>
              </div>
            </div>
          </div>

          <div className="shsp-insights">
            <div className="shsp-card shsp-rise" style={{ animationDelay: "300ms" }}>
              <div className="shsp-card__head">
                <div>
                  <h3>Participation by class</h3>
                  <p>Attempts from each class, split by quiz status</p>
                </div>
                <div className="shsp-legend">
                  <span><i style={{ "--c": COMPLETED }} />Completed</span>
                  <span><i style={{ "--c": IN_PROGRESS }} />In progress</span>
                </div>
              </div>
              <ClassChart classes={classes} />
              <div className="shsp-chart__caption">Class</div>
            </div>

            <div className="shsp-card shsp-rise" style={{ animationDelay: "360ms" }}>
              <div className="shsp-card__head">
                <div>
                  <h3>Quiz status</h3>
                  <p>How far students have got</p>
                </div>
              </div>
              <StatusDonut completed={summary.completed} attempts={summary.attempts} />
              <div className="shsp-timeline">
                <div className="shsp-timeline__item">
                  <span className="shsp-timeline__icon"><FiCalendar /></span>
                  <div><small>First participation</small><b>{fmtStamp(summary.firstAt)}</b></div>
                </div>
                <div className="shsp-timeline__item">
                  <span className="shsp-timeline__icon"><FiClock /></span>
                  <div><small>Latest participation</small><b>{fmtStamp(summary.lastAt)}</b></div>
                </div>
              </div>
            </div>
          </div>

          <div className="shsp-card shsp-register shsp-rise" style={{ animationDelay: "420ms" }}>
            <div className="shsp-register__head">
              <div>
                <h3>Participation register</h3>
                <p>{"Newest first. Names are shown as the students typed them."}</p>
              </div>
              <div className="shsp-tools">
                <label className="shsp-search">
                  <FiSearch />
                  <input type="search" placeholder="Search by student name" aria-label="Search by student name" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
                  {search && (
                    <button type="button" className="shsp-search__clear" aria-label="Clear search" onClick={() => { setSearch(""); setPage(1); }}><FiX /></button>
                  )}
                </label>
                <select className="shsp-select" aria-label="Filter by class" value={klass} onChange={(e) => { setKlass(e.target.value); setPage(1); }}>
                  <option value="">All classes</option>
                  {CLASSES.map((c) => <option key={c} value={c}>{classLabel(c)}</option>)}
                </select>
                <span className="shsp-count">{isFetching ? "Loading…" : plural(total, "record")}</span>
              </div>
            </div>

            <div className="shsp-tablewrap">
              <table className="shsp-table">
                <thead>
                  <tr><th>#</th><th>Student</th><th>Class</th><th>Participated on</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {!loaded ? (
                    [0, 1, 2, 3].map((k) => (
                      <tr key={k} className="is-skeleton">
                        <td className="shsp-idx"><i className="shsp-skel" style={{ width: 18 }} /></td>
                        <td className="shsp-td-person"><div className="shsp-person"><i className="shsp-skel shsp-skel--avatar" /><i className="shsp-skel" style={{ width: 140 }} /></div></td>
                        <td className="shsp-td-class"><i className="shsp-skel" style={{ width: 64 }} /></td>
                        <td className="shsp-td-when"><i className="shsp-skel" style={{ width: 120 }} /></td>
                        <td className="shsp-td-status"><i className="shsp-skel" style={{ width: 86 }} /></td>
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr className="is-empty">
                      <td colSpan={5}>
                        <div className="shsp-empty">
                          <svg viewBox="0 0 160 120" aria-hidden="true">
                            <ellipse cx="80" cy="108" rx="54" ry="7" fill="#e8f2ea" />
                            <rect x="44" y="18" width="72" height="86" rx="10" fill="#fff" stroke="#cfe5d5" strokeWidth="2" />
                            <rect x="58" y="10" width="44" height="16" rx="6" fill="#2fb457" />
                            <path d="M58 46h44M58 62h44M58 78h28" stroke="#dcebdf" strokeWidth="6" strokeLinecap="round" />
                            <circle cx="112" cy="84" r="17" fill="#fff3e2" stroke="#f0a53a" strokeWidth="3" />
                            <path d="M124 96l10 10" stroke="#f0a53a" strokeWidth="5" strokeLinecap="round" />
                          </svg>
                          <h4>{filtered ? "No students match this search" : SWACHHATA_QUIZ_OPEN ? "No student has taken the quiz yet" : "No student from your school took the quiz"}</h4>
                          <p>{filtered ? "Try a different name or choose All classes." : SWACHHATA_QUIZ_OPEN ? "Share the quiz link and your UDISE code with your students. Their names appear here as soon as they start." : "The Swachhata Hi Seva quiz is now closed."}</p>
                          {filtered ? (
                            <button type="button" className="shsp-btn shsp-btn--soft" onClick={() => { setSearch(""); setKlass(""); setPage(1); }}><FiX /><span>Clear filters</span></button>
                          ) : SWACHHATA_QUIZ_OPEN ? (
                            <CopyButton text={shareText} label="Copy invite message" />
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, i) => (
                      <tr key={r.attemptId} className={isFetching ? "is-stale" : ""}>
                        <td className="shsp-idx">{(page - 1) * PAGE_SIZE + i + 1}</td>
                        <td className="shsp-td-person">
                          <div className="shsp-person">
                            <span className={`shsp-avatar shsp-avatar--${toneOf(r.name)}`}>{initials(r.name)}</span>
                            <div>
                              <b>{r.name}</b>
                              <small>{r.age ? `Age ${r.age}` : "Age not given"}</small>
                            </div>
                          </div>
                        </td>
                        <td className="shsp-td-class"><span className="shsp-classpill">{classLabel(r.class)}</span></td>
                        <td className="shsp-td-when">
                          <div className="shsp-when"><b>{fmtDay(r.participatedAt)}</b><small>{fmtTime(r.participatedAt)}</small></div>
                        </td>
                        <td className="shsp-td-status">
                          <span className={`shsp-status shsp-status--${r.status === "submitted" ? "done" : "open"}`}>
                            <i />{r.status === "submitted" ? "Completed" : "In progress"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {total > 0 && (
              <div className="shsp-pager">
                <span>{`Showing ${nf(from)}–${nf(to)} of ${plural(total, "record")}`}</span>
                <div className="shsp-pages">
                  <button type="button" className="shsp-page" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="Previous page"><FiChevronLeft /><span>Previous</span></button>
                  {pageWindow(page, totalPages).map((p) => (
                    <button key={p} type="button" className={`shsp-page shsp-page--num ${p === page ? "is-active" : ""}`} aria-current={p === page ? "page" : undefined} onClick={() => setPage(p)}>{p}</button>
                  ))}
                  <button type="button" className="shsp-page" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} aria-label="Next page"><span>Next</span><FiChevronRight /></button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
